import { Progress } from '@components/Progress'
import {
  CONTINUE_CARD_DIMENSIONS,
  CONTINUE_CARD_DIMENSIONS_DESKTOP_WEB,
  CONTINUE_CARD_DIMENSIONS_MODAL,
} from '@constants/continueCardDimensions'
import { useMediaSize } from '@hooks/useMediaSize'
import { LessonTypeTranslation } from '@utils/lessonMediaType'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Animated } from 'react-native'
import { View, Text, useMedia, isWeb } from 'tamagui'
import { CoverImage } from '@components/CoverImage'
import { LinearGradient } from 'expo-linear-gradient'
import { useOpacityAnimation } from '@hooks/useOpacityAnimation'
import { ExtendedCardContent } from './ExtendedCardContent'
import { HeroIcon } from '@components/HeroIcon'
import { MobileExtendedCardContent } from './MobileExtendedCardContent'
import { IContinueLessons } from '@dtos/User'
import { useTranslation } from 'react-i18next'
import { getVideoThumb } from '@utils/videoThumbs'
import { useScaleAnimation } from '@hooks/useScaleAnimation'
import { router } from 'expo-router'
import { useCurrentLesson } from '@hooks/useCurrentLesson'
import { useHasFeature } from '@hooks/useThemeParams'
import { useThemeColor } from '@hooks/useThemeColor'
import { MEMBERS_PAGE_COURSE_CONTENTS } from '@constants/features'
import {
  courseContentPath,
  heroThemeCourseContentPath,
} from '@utils/coursePaths'
import { useAdminPreview } from '@hooks/useAdminPreview'
import { IProgressCard } from '@dtos/Enrollment'
import { ICourse, ILesson } from '@dtos/Course'
import {
  REDIRECT_TO_CONTENT_TYPES,
  productRedirectToEnrollment,
} from '@utils/productRedirectToEnrollment'
import { ProductBadge } from '../MyProducts/ProductCard/ProductBadge'

interface IContinueCardProps {
  lessonProgress?: IContinueLessons
  shouldExtendCard: boolean
  isModalVersion?: boolean
  showProductName: boolean
  productTitle: string
  cardId: string
  customOnPress?: () => Promise<void>
  customThumbnail?: string
  displayPlayIcon?: boolean
  displayBuyTag?: boolean
  /** Selo "Bloqueado": produto que o aluno não tem e que o app não vende
   * (app da loja, ver `@constants/storeRules`). */
  displayLockTag?: boolean
  closeModal?: () => void
  progressCard?: IProgressCard
  usedAsProduct?: boolean
  course?: ICourse
  firstLesson?: ILesson
  enrollmentId?: string
}

export function ContinueCard({
  lessonProgress,
  shouldExtendCard,
  isModalVersion = false,
  showProductName,
  productTitle,
  cardId,
  customOnPress,
  customThumbnail,
  displayPlayIcon = true,
  displayBuyTag = false,
  displayLockTag = false,
  closeModal,
  progressCard,
  usedAsProduct = false,
  course,
  firstLesson,
  enrollmentId,
  ...extraProps
}: IContinueCardProps) {
  const { setCurrentLesson } = useCurrentLesson()
  const hasCourseContentsPage = useHasFeature(MEMBERS_PAGE_COURSE_CONTENTS)
  const pageFade = useThemeColor('bg-page', 0.9)
  const { isPreview } = useAdminPreview()
  const lesson = lessonProgress?.lesson
  const [isHovered, setIsHovered] = useState(false)
  const { t } = useTranslation('CourseDetails')
  const tamaguiMedia = useMedia()
  const size = useMediaSize()
  const isYoutubeOrVimeo = ['Youtube', 'Vimeo'].includes(
    lesson?.media?.host || '',
  )
  const hasThumbnail =
    lesson?.coverImage?.cdnFileUrl || isYoutubeOrVimeo || customThumbnail
  const isActivity = Object.keys(lesson?.activity || {}).length > 0
  const lessonType = isActivity ? 'Quiz' : lesson?.media?.type || 'Text'
  const lessonTypeIcon = LessonTypeTranslation(lessonType)
  const { interpolatedOpacity, startAnimation } = useOpacityAnimation(300)
  const isAVideo = lesson?.media?.type === 'Video'
  const displayProgress =
    (!shouldExtendCard || isModalVersion) && isAVideo && lessonProgress
  const {
    startAnimation: startScaleAnimation,
    endAnimation: endScaleAnimation,
    scaleValues,
  } = useScaleAnimation(cardId)
  const displayTitleOnHover = !shouldExtendCard
  const isMobile = size === 'sm' || size === 'xs'
  const extendedCardOnMobile = isMobile && isModalVersion
  const bgColor = isModalVersion ? '$bg-surface-sunken' : '$bg-surface'
  const cardGap = extendedCardOnMobile ? 12 : 0
  const cardBorderRadius = !extendedCardOnMobile || isModalVersion ? 8 : 0
  const imageBorderRadius =
    extendedCardOnMobile || !tamaguiMedia.gtMd || shouldExtendCard ? 8 : 0
  const progressBarBorderRadius = extendedCardOnMobile ? 8 : 0
  const animatedOpacity = tamaguiMedia.gtMd ? interpolatedOpacity : 1
  const playIconOpacity = tamaguiMedia.gtMd ? 1 : 0
  const iconPaddingBottom = tamaguiMedia.gtMd ? 0 : 8
  const displayBottomCardTitle =
    showProductName && !shouldExtendCard && !isModalVersion
  const cardFlexDirection = displayBottomCardTitle ? 'column' : 'row'
  const progressBarHeight = tamaguiMedia.gtSm && !extendedCardOnMobile ? 8 : 4
  const borderCardWidth = tamaguiMedia.gtSm ? 2 : 0
  const title = lesson?.title || ''
  const productName = lessonProgress?.schoolProduct.title || ''

  const cardTitlePadding = useMemo(() => {
    if (tamaguiMedia.gtMd) return 24
    if (tamaguiMedia.gtSm) return 16
    return 12
  }, [tamaguiMedia.gtMd, tamaguiMedia.gtSm])

  const titleBottomPosition = useMemo(() => {
    if (!tamaguiMedia.gtMd) return -5
    return isHovered ? 0 : -20
  }, [tamaguiMedia.gtMd, isHovered])

  const continueCardDimensions = useMemo(() => {
    if (extendedCardOnMobile) return CONTINUE_CARD_DIMENSIONS.modal
    return CONTINUE_CARD_DIMENSIONS[size] || CONTINUE_CARD_DIMENSIONS.xs
  }, [extendedCardOnMobile, size])

  const cardContainerDimensions = useMemo(() => {
    if (extendedCardOnMobile) return CONTINUE_CARD_DIMENSIONS_MODAL[size]
    if (shouldExtendCard)
      return {
        width: isModalVersion
          ? CONTINUE_CARD_DIMENSIONS_MODAL[size].width
          : CONTINUE_CARD_DIMENSIONS_DESKTOP_WEB[size].width,
        height: continueCardDimensions.height,
      }
    return continueCardDimensions
  }, [
    extendedCardOnMobile,
    shouldExtendCard,
    isModalVersion,
    size,
    continueCardDimensions,
  ])

  const iconSize = useMemo(() => {
    if (extendedCardOnMobile) return 28
    return 36
  }, [extendedCardOnMobile])

  const cardBorderColor = useMemo(() => {
    if (extendedCardOnMobile) return 'transparent'
    return isHovered ? '$border-hover' : 'transparent'
  }, [extendedCardOnMobile, isHovered])

  const thumbnail = useMemo(() => {
    if (customThumbnail)
      return {
        cdnFileUrl: customThumbnail,
      }
    return lesson?.coverImage || null
  }, [customThumbnail, lesson?.coverImage])

  const handleProductLessonPress = useCallback(() => {
    if (!course || !enrollmentId) return

    const isContentType = !!(
      course?.kind && REDIRECT_TO_CONTENT_TYPES.includes(course.kind)
    )

    // Para tipos content-first (ebook, file, webinar), exige firstLesson
    if (isContentType && !firstLesson) return

    if (closeModal) closeModal()

    if (isContentType && firstLesson) {
      setCurrentLesson({
        ...firstLesson,
        media: firstLesson.media ?? null,
      })
    }

    productRedirectToEnrollment({
      enrollmentId,
      courseId: course.id.toString(),
      courseContentId:
        isContentType && firstLesson
          ? Number(firstLesson.courseContentId)
          : undefined,
      kind: course?.kind,
      hasCourseContentsPage,
      isPreview,
    })
  }, [
    course,
    firstLesson,
    enrollmentId,
    closeModal,
    setCurrentLesson,
    hasCourseContentsPage,
    isPreview,
  ])

  const handleLessonPress = useCallback(async () => {
    if (customOnPress) {
      await customOnPress()
      return
    }

    if (usedAsProduct) {
      handleProductLessonPress()
      return
    }

    const courseId = lessonProgress?.progressCard.courseId
    const lessonId = lesson?.courseContentId

    if (!lessonId || !courseId) return

    if (closeModal) closeModal()
    setCurrentLesson({
      ...lesson,
      media: lesson?.media ?? null,
    })

    // Escolas sem a feature members_page_course_contents não têm a página de
    // consumo no SPA de membros — navegar via router deixaria a URL 404 no
    // refresh. No web, redireciona (full load) para a página legada do
    // hero_theme (sem /online_course). No nativo, sempre usa o router.
    if (isWeb && !isPreview && !hasCourseContentsPage) {
      window.location.href = heroThemeCourseContentPath({
        enrollmentId: lessonProgress?.enrollment.id ?? '',
        courseId,
        courseContentId: lessonId,
      })
      return
    }

    const formattedPath = courseContentPath({
      isPreview,
      enrollmentId: lessonProgress?.enrollment.id,
      courseId,
      courseContentId: lessonId,
      moduleId: lessonProgress?.moduleId,
    })

    router.navigate(formattedPath)
  }, [
    customOnPress,
    usedAsProduct,
    handleProductLessonPress,
    lessonProgress?.progressCard.courseId,
    lessonProgress?.enrollment.id,
    lessonProgress?.moduleId,
    lesson,
    closeModal,
    setCurrentLesson,
    hasCourseContentsPage,
    isPreview,
  ])

  const productType = useMemo(() => {
    return t(
      `product_type.${lessonProgress?.schoolProduct.courses?.[0]?.kind || 'online_course'}`,
    )
  }, [lessonProgress?.schoolProduct.courses, t])

  const badgeIconSize = useMemo(() => {
    if (tamaguiMedia.gtLg) return 20
    if (tamaguiMedia.gtSm) return 16
    return 12
  }, [tamaguiMedia.gtLg, tamaguiMedia.gtSm])

  useEffect(() => {
    startAnimation(isHovered)
  }, [isHovered, startAnimation])

  const cardContainerWidth = isWeb
    ? `${cardContainerDimensions.width}px !important`
    : cardContainerDimensions.width

  const handleHoverIn = useCallback(() => {
    if (tamaguiMedia.gtMd) setIsHovered(true)
  }, [tamaguiMedia.gtMd])

  const handleHoverOut = useCallback(() => {
    if (tamaguiMedia.gtMd) setIsHovered(false)
  }, [tamaguiMedia.gtMd])

  const handleMouseEnter = useCallback(() => {
    startScaleAnimation(cardId)
  }, [startScaleAnimation, cardId])

  const handleMouseLeave = useCallback(() => {
    endScaleAnimation(cardId)
  }, [endScaleAnimation, cardId])

  return (
    <View
      borderRadius={cardBorderRadius}
      {...extraProps}
      maxWidth={cardContainerWidth}
      minWidth={cardContainerWidth}
      height={cardContainerDimensions.height}
    >
      <View
        borderRadius={cardBorderRadius}
        borderWidth={borderCardWidth}
        borderColor={cardBorderColor}
        overflow="hidden"
        maxWidth={cardContainerWidth}
        minWidth={cardContainerWidth}
        minHeight={cardContainerDimensions.height}
        height="100%"
        testID="continue-card"
        onPress={displayLockTag ? undefined : handleLessonPress}
        {...(displayLockTag && { accessibilityState: { disabled: true } })}
        {...(!isWeb &&
          !displayLockTag && {
            pressStyle: { opacity: 0.7, scale: 0.98 },
            animation: 'quick',
          })}
        onHoverIn={handleHoverIn}
        onHoverOut={handleHoverOut}
        flexDirection={cardFlexDirection}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        cursor={displayLockTag ? 'default' : 'pointer'}
        gap={cardGap}
        style={{
          transition: 'all 0.7s ease',
        }}
      >
        <View
          position="relative"
          backgroundColor="$bg-surface"
          borderRadius={cardBorderRadius}
        >
          {displayBuyTag && (
            <ProductBadge
              variant="buy"
              badgeIconSize={badgeIconSize}
              label={t('productCard.badges.buy')}
            />
          )}
          {displayLockTag && !displayBuyTag && (
            <ProductBadge
              variant="lock"
              badgeIconSize={badgeIconSize}
              label={t('productCard.badges.locked')}
            />
          )}
          <Animated.View
            style={{
              transform: [
                {
                  scale: scaleValues[cardId] || 1,
                },
              ],
            }}
          >
            {hasThumbnail ? (
              <CoverImage
                testID="thumbnail-image"
                uri={
                  getVideoThumb({
                    ...(lesson?.media || {
                      type: 'Video',
                      id: 1,
                      title: '',
                      duration: 0,
                      fileUrl: null,
                      fileExt: null,
                      text: null,
                      hostKey: null,
                      host: null,
                      organizationUuid: null,
                    }),
                    coverImage: thumbnail,
                  }) || undefined
                }
                width={continueCardDimensions.width}
                height={continueCardDimensions.height}
                borderRadius={imageBorderRadius}
                alt={lesson?.title || ''}
              />
            ) : (
              <View
                testID="media-icon"
                width={continueCardDimensions.width}
                height={continueCardDimensions.height}
                bg={bgColor}
                alignItems="center"
                justifyContent="center"
                borderRadius="$border-radius-sm"
              >
                <HeroIcon
                  icon={lessonTypeIcon}
                  size={iconSize}
                  color="$accent-hover"
                />
              </View>
            )}
          </Animated.View>
          {displayProgress && (
            <View
              testID="progress-bar"
              position="absolute"
              bottom={0}
              left={0}
              right={0}
              overflow="hidden"
              zIndex={200}
              width={continueCardDimensions.width}
              borderBottomRightRadius={progressBarBorderRadius}
              borderBottomLeftRadius={progressBarBorderRadius}
            >
              <Progress
                height={progressBarHeight}
                borderRadius={0}
                value={lessonProgress?.progress || 0}
                foregroundColor="$progress-fill"
              />
            </View>
          )}
          {!extendedCardOnMobile && (
            <Animated.View
              style={{
                position: 'absolute',
                width: continueCardDimensions.width,
                bottom: 0,
                paddingHorizontal: cardTitlePadding,
                justifyContent: 'center',
                alignItems: 'center',
                alignContent: 'center',
                zIndex: 100,
                height: 55,
                opacity: animatedOpacity,
              }}
            >
              {displayPlayIcon && (
                <View
                  backgroundColor="rgba(82, 82, 82, 0.9)"
                  position="absolute"
                  alignItems="center"
                  justifyContent="center"
                  bottom={65}
                  height={55}
                  width={55}
                  borderRadius={100}
                  opacity={playIconOpacity}
                >
                  <HeroIcon
                    icon={['fas', 'play']}
                    size={24}
                    color="$text-on-media"
                    style={{
                      paddingBottom: iconPaddingBottom,
                    }}
                  />
                </View>
              )}
              {displayTitleOnHover && (
                <>
                  <Text
                    ff="$mulishSemiBold"
                    fontSize="$font-size-xxxs"
                    fontWeight={600}
                    color="$text-muted"
                    numberOfLines={1}
                    position="relative"
                    zIndex={101}
                    bottom={titleBottomPosition}
                    transition="bottom 0.5s ease"
                    $gtSm={{
                      fontSize: '$font-size-xs',
                    }}
                  >
                    {productTitle}
                  </Text>
                  <LinearGradient
                    colors={['transparent', pageFade]}
                    style={{
                      position: 'absolute',
                      left: 0,
                      bottom: 0,
                      right: 0,
                      height: 55,
                      zIndex: 100,
                    }}
                  />
                </>
              )}
            </Animated.View>
          )}
        </View>
        {displayBottomCardTitle && (
          <View
            padding={cardTitlePadding}
            backgroundColor="$bg-surface"
            width={cardContainerDimensions.width}
            flex={1}
            borderBottomLeftRadius={imageBorderRadius}
            borderBottomRightRadius={imageBorderRadius}
          >
            <Text
              ff="$mulishSemiBold"
              fontSize="$font-size-xxxs"
              flexWrap="wrap"
              color="$text-primary"
              numberOfLines={2}
              ellipsizeMode="tail"
              width="100%"
              $gtSm={{
                fontSize: '$font-size-xs',
              }}
            >
              {title}
            </Text>
          </View>
        )}
        {extendedCardOnMobile ? (
          <MobileExtendedCardContent
            title={lesson?.title || productTitle}
            productName={productName}
            dataProgress={
              progressCard
                ? {
                    progress: progressCard.progress,
                    completedLessons: progressCard.completed,
                    totalLessons: progressCard.total,
                  }
                : null
            }
          />
        ) : (
          shouldExtendCard && (
            <ExtendedCardContent
              title={title}
              progress={lessonProgress?.progress || 0}
              productName={productName}
              productType={productType}
              contentType={lesson?.media?.type || null}
            />
          )
        )}
      </View>
    </View>
  )
}
